import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getResponse } from 'services/api';
import { useToastNotification } from 'hooks/useToastNotification';
import { useUpdateMapping } from '../../hooks';
import { useWizard } from '../../store';
import { ScopeMode } from '../../types';
import {
	Button,
	Card,
	CardDescription,
	CardTitle,
	Footer,
	Input,
	Label,
	Row,
	Select,
} from '../../TicketImporter.styles';

interface ApiEvent {
	id?: number;
	ID?: number;
	name: string;
	start_date?: string;
	event_days?: Array<{ start_date: string }>;
}

const eventID = (e: ApiEvent): number | undefined => e.id ?? e.ID;

const useEventsList = (enabled: boolean, search: string) =>
	useQuery({
		queryKey: ['ticket-importer-events', search],
		enabled,
		queryFn: async () => {
			// date=all bypasses the default "recent" filter (±1 week from now)
			// so the wizard can target past events being imported retroactively.
			const { data } = await getResponse(`events?per_page=500&date=all&search=${encodeURIComponent(search)}`);
			return data as ApiEvent[];
		},
	});

export const ScopeStep = (): JSX.Element => {
	const history = useHistory();
	const { pathname } = useLocation();
	const { state, dispatch } = useWizard();
	const { showNotification, TYPES } = useToastNotification();

	const [scope, setScope] = useState<ScopeMode>(state.scopeMode);
	const [eventId, setEventId] = useState<number | null>(state.fixedEventId);
	const [search, setSearch] = useState('');

	const { data: events, isLoading } = useEventsList(scope === 'fixed', search);

	const { mutate, isLoading: saving } = useUpdateMapping(state.importId as number, {
		onSuccess: () => {
			dispatch({ type: 'patch', payload: { scopeMode: scope, fixedEventId: eventId } });
			history.push(`${pathname}#3`);
		},
		onError: () => showNotification({ type: TYPES.ERROR, message: 'Could not save mapping' }),
	});

	const onContinue = () => {
		if (!state.importId) return;
		if (scope === 'fixed' && !eventId) {
			showNotification({ type: TYPES.ERROR, message: 'Pick an event to continue' });
			return;
		}
		mutate({
			scope_mode: scope,
			event_id: scope === 'fixed' ? eventId : null,
			source_platform: state.sourcePlatform || undefined,
		});
	};

	return (
		<>
			<Card>
				<CardTitle>2. Scope</CardTitle>
				<CardDescription>
					Choose whether every row in the CSV maps to a single event you pick now, or each row resolves to its own event
					by matching the <code>event_name</code> and <code>event_date</code> columns.
				</CardDescription>

				<Row>
					<Label>
						<input type="radio" name="scope" checked={scope === 'fixed'} onChange={() => setScope('fixed')} />{' '}
						<strong>Single event</strong> — pick an event below, every row imports into it.
					</Label>
				</Row>
				<Row>
					<Label>
						<input type="radio" name="scope" checked={scope === 'per_row'} onChange={() => setScope('per_row')} />{' '}
						<strong>Per-row</strong> — each row matches an event by name + date (use when the CSV spans multiple
						events).
					</Label>
				</Row>

				{scope === 'fixed' && (
					<>
						<Row>
							<Label>
								Search events
								<Input
									type="text"
									placeholder="Type to filter…"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</Label>
						</Row>
						<Row>
							<Label>
								Event<span className="req">*</span>
								<Select
									value={eventId ?? ''}
									onChange={(e) => setEventId(e.target.value ? Number(e.target.value) : null)}
								>
									<option value="">{isLoading ? 'Loading…' : 'Select an event'}</option>
									{events
										?.map((ev) => ({ ev, id: eventID(ev) }))
										.filter(({ id }) => typeof id === 'number')
										.map(({ ev, id }) => {
											const date = ev.start_date || ev.event_days?.[0]?.start_date;
											return (
												<option key={id} value={id}>
													{ev.name}
													{date ? ` — ${new Date(date).toLocaleDateString()}` : ''}
												</option>
											);
										})}
								</Select>
							</Label>
						</Row>
					</>
				)}
			</Card>

			<Footer>
				<Button variant="secondary" onClick={() => history.push(`${pathname}#1`)}>
					← Back
				</Button>
				<Button onClick={onContinue} disabled={saving}>
					Continue →
				</Button>
			</Footer>
		</>
	);
};
